import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiService } from '../services/api.service';
import type { PermissionAction } from '../config/navigation';

export interface SessionPermissions {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    branchId?: string | null;
    isActive: boolean;
  };
  modules: Record<string, Record<string, boolean>>;
  critical: Record<string, boolean>;
  allowedBranchIds: string[] | null;
  allowedLegalEntityIds?: string[] | null;
  hasExplicitGrants: boolean;
}

interface PermissionsContextValue {
  session: SessionPermissions | null;
  loading: boolean;
  can: (module: string, action?: PermissionAction) => boolean;
  canCritical: (point: string) => boolean;
  refresh: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

const STORAGE_KEY = 'sessionPermissions';

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionPermissions | null>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(Boolean(localStorage.getItem('authToken')));

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('authToken')) {
      setSession(null);
      sessionStorage.removeItem(STORAGE_KEY);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.getMyPermissions();
      setSession(data);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      setSession(null);
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const can = useCallback(
    (module: string, action: PermissionAction = 'read') => {
      if (!session) return false;
      if (session.user.role === 'ADMIN') return true;
      return session.modules[module]?.[action] ?? false;
    },
    [session],
  );

  const canCritical = useCallback(
    (point: string) => {
      if (!session) return false;
      if (session.user.role === 'ADMIN') return true;
      return session.critical[point] ?? false;
    },
    [session],
  );

  const value = useMemo(
    () => ({ session, loading, can, canCritical, refresh }),
    [session, loading, can, canCritical, refresh],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions debe usarse dentro de PermissionsProvider');
  return ctx;
}
